import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import L from 'leaflet';
import { Observable, Observer } from 'rxjs';
import { LayerThemeData, LegendItem } from '../data-types/map-layer-info';
import { ModalItem } from '../elements/modal-item';
import { getFlussiPopUpContent, getParameterIfFilterVisible, getValues, hasValues } from './utils';
import { WmsLayer } from './WmsLayer';
import { NonTiledLayerWMS } from './NonTiledLayer';

export class DynamicLegendWmsLayer implements WmsLayer {
	name: string;
	visibleFilterIds: string[];
	modal: ModalItem;
	themes: LayerThemeData[];
	selected: boolean;
	legend: LegendItem[];

	intervalIcons = [
		'./assets/images/interval1.jpg',
		'./assets/images/interval2.jpg',
		'./assets/images/interval3.jpg',
		'./assets/images/interval4.jpg',
		'./assets/images/interval5.jpg',
	];

	constructor(
		name: string,
		visibleFilterIds: string[],
		modal: ModalItem,
		themes: LayerThemeData[],
		selected: boolean,
		colorLegend: LegendItem[],
	) {
		this.name = name;
		this.visibleFilterIds = visibleFilterIds;
		this.modal = modal;
		this.themes = themes;
		this.selected = selected;
		this.legend = colorLegend;
	}

	getAsWms = (filters: {
		[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
	}): L.Layer[] => {
		this.updateLegend(filters);
		console.log(this.visibleFilterIds);

		/* ---- */
		/*

		Solo per flussi è necessario gestire il caso in cui con il filtro mese attivo chiamiamo un layer mentre con il
		filtro giorno attivo ne chiamiamo un altro. Qui di seguito modifichiamo la lista dei layer su cui ciclare in base
		a quale filtro è attualmente selezionato. I layer definiti in hardcoded.ts di flussi devono rimanere in quell'ordine 
		per permettere a questa istruzione di funzionare correttamente.

		Questa implementazione non è versatile nè generalizzata, è stata apportata in seguito all'efficientamento dei layer di flussi su 
		geoserver: si è deciso di dividere la query in due per velocizzare il caricamento del layer con i filtro mese. Ora i layer utilizzano
		due tabelle differenti di partenza (una aggregata per mese e una per giorno).

		*/
		let themes = [];
		if (filters['mese'] !== undefined) {
			themes = [this.themes[0]];
		} else {
			themes = [this.themes[1]];
		}

		/* ---- */

		let result = [];
		for (let theme of themes) {
			let layer = null;
			if (theme.tiled !== undefined && !theme.tiled) {
				layer = new NonTiledLayerWMS(`${environment.contextRoot}/api/wms?` + this.getWMSQuery(filters), {
					maxZoom: 18,
					attribution: '...',
					transparent: true,
					format: 'image/png',
					zIndex: 1000,
					layers: theme.name,
					styles: theme.style,
				});
			} else {
				layer = new L.TileLayer.WMS(`${environment.contextRoot}/api/wms?` + this.getWMSQuery(filters), {
					maxZoom: 18,
					attribution: '...',
					transparent: true,
					format: 'image/png',
					zIndex: 1000,
					layers: theme.name,
					styles: theme.style,
				});
			}

			result.push(layer);
		}

		return result;
	};

	/**
	 * Prepares the wms query parameter from the selected layer and the parent's filters selected values.
	 * * Tutti i filtri tranne numero strada e nome strada vogliono una @ all'inizio e alla fine e vengono separati da '@@' se ci sono più valori
	 * * alcuni, poi, come valore nullo vogliono cose specifiche (invece di ""): numStrada -> '000', limPortata -> '@@', limSagoma -> '@@', limTipologia -> '@@'
	 * @returns
	 */
	private getWMSQuery = (filters: {
		[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
	}) => {
		let boundaries = this.getLegendBoundaries(filters);
		let viewparams = [];
		console.log(filters);

		const filterIDsToApply = ['postazioni', 'provincia', 'comune', 'strade'];

		for (let filter of filterIDsToApply) {
			viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, filter));
		}

		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'giorno', "''", '', "','", ''));
		viewparams.push(getParameterIfFilterVisible(filters, this.visibleFilterIds, 'mese', "''", '', "','", ''));

		viewparams.push(this.boundariesToWMSParams(boundaries));

		let monthsOrDaysSelected = hasValues('giorno', filters) != '0' || hasValues('mese', filters) != '0';
		viewparams.push(`MONTHS_OR_DAYS_SELECTED:${monthsOrDaysSelected ? '1' : '0'}`);

		return `viewparams=` + viewparams.join(';');
	};

	/**
	 * Builds XML for legend boundaries
	 * @param boundaries
	 * @returns xml as string
	 */
	private boundariesToWMSParams = (boundaries: number[]): string => {
		return boundaries.map((b, i) => `ESTREMO_${i}:${b}`).join(';');
	};

	/**
	 * Builds legend boundaries based on the passed filters
	 * @returns an array containing boundaries
	 */
	private getLegendBoundaries = (filters: {
		[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
	}): number[] => {
		let selectedTimeInterval: string = 'mese' in filters ? 'mese' : 'giorno';

		let baseBoundaries: { [timeInterval: string]: number[] } = {
			giorno: [0, 5000, 8000, 12000, 18000],
			mese: [0, 200000, 400000, 600000, 800000],
		};

		let actualBoundaries = [];
		let selectedCount = filters[selectedTimeInterval].selectedPrimaryValues.length;

		for (let b of baseBoundaries[selectedTimeInterval]) {
			actualBoundaries.push(b * selectedCount);
		}

		return actualBoundaries;
	};

	/**
	 * Build the new legend based on the time interval selected in the filters and the number of months/days selected.
	 * @param filters
	 */
	private updateLegend = (filters: {
		[filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] };
	}) => {
		let boundaries = this.getLegendBoundaries(filters);

		// generate new legend
		this.legend = [];
		for (let i = 0; i < boundaries.length - 1; i++) {
			let label = `${boundaries[i].toLocaleString('de-DE')} - ${boundaries[i + 1].toLocaleString('de-DE')}`;
			let icon = this.intervalIcons[i];
			let legendItem: LegendItem = {
				label: label,
				iconPath: icon,
			};
			this.legend.push(legendItem);
		}

		// create last boundary as "> max"
		let lastLegendItem: LegendItem = {
			label: `> ${boundaries.at(-1)!.toLocaleString('de-DE')}`,
			iconPath: this.intervalIcons.at(-1),
		};
		this.legend.push(lastLegendItem);
	};

	getPopup = (
		clickedPoint: L.LatLng,
		filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
		http: HttpClient,
		zoom: number,
	): Observable<L.Popup> => {
		var toPoint = L.Projection.SphericalMercator.project(clickedPoint);

		var params = this.getPopUpParams(toPoint, filters, zoom);

		// This function runs when subscribe() is called
		function popupSubscriber(observer: Observer<L.Popup>) {
			http.get<any>(`${environment.contextRoot}/api/flussi/map/popup`, {
				responseType: 'json',
				params: params,
			}).subscribe((data) => {
				if (data.length > 0) {
					let actualLatLng = L.Projection.SphericalMercator.unproject(
						new L.Point(data[0].geometry.coordinates[0], data[0].geometry.coordinates[1]),
					);

					observer.next(L.popup().setLatLng(actualLatLng).setContent(getFlussiPopUpContent(data[0])));
				}
				observer.complete();
			});

			return { unsubscribe() {} };
		}

		return new Observable(popupSubscriber);
	};

	private getPopUpParams = (
		point: L.Point,
		filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
		zoom: number,
	) => {
		let result = new HttpParams();
		result = result.append('lat', point.y);
		result = result.append('lon', point.x);
		result = result.append('srs', 'EPSG:3785');
		result = result.append('zoomlevel', Math.round(zoom));
		result = result.append('filter_giorno', hasValues('giorno', filters));
		result = result.append('input_giorno', getValues('giorno', filters));
		result = result.append('filter_mese', hasValues('mese', filters));
		result = result.append('input_mese', getValues('mese', filters));
		result = result.append('filter_postazioni', hasValues('postazioni', filters));
		result = result.append('input_postazioni', getValues('postazioni', filters));
		result = result.append('filter_provincia', hasValues('provincia', filters));
		result = result.append('input_provincia', getValues('provincia', filters));
		result = result.append('filter_comune', hasValues('comune', filters));
		result = result.append('input_comune', getValues('comune', filters));
		result = result.append('filter_strade', hasValues('strade', filters));
		result = result.append('input_strade', getValues('strade', filters));
		return result;
	};
}
